import { InputWithSign } from '@/modules/common/components/input-with-sign';
import React, { useRef } from 'react';
import styles from './index.module.sass';
import { handleClick } from '@/modules/helpers/handler-click/index.helper';
import { processNumberInput } from '@/modules/helpers/processNumberInput/processNumberInput';

interface RangeSliderProps {
  min: string; // Минимальное значение
  max: string; // Максимальное значение
  value: [string, string]; // Текущее значение (от и до)
  setValue: (value: [string, string]) => void; // Функция для обновления
  fieldLabel: string; // Метка поля (например, "Клейковина")
  postfix?: string; // Постфикс значений (например, "%")
}

export const RangeSlider: React.FC<RangeSliderProps> = ({
  min,
  max,
  value,
  setValue,
  fieldLabel,
  postfix = '',
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: MouseEvent, thumbIndex: number) => {
    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const sliderWidth = rect.width;
    const clickPosition = Math.min(Math.max(e.clientX - rect.left, 0), sliderWidth);

    const percentage = clickPosition / sliderWidth;
    const newValue = Math.round(
      parseFloat(min) + percentage * (parseFloat(max) - parseFloat(min)),
    );
    const updatedValues = [...value] as [string, string];

    if (thumbIndex === 0) {
      updatedValues[0] = String(Math.min(newValue, parseFloat(value[1]) - 1));
    } else {
      updatedValues[1] = String(Math.max(newValue, parseFloat(value[0]) + 1));
    }

    setValue(updatedValues);
  };

  const handleThumbMouseDown = (thumbIndex: number) => {
    document.body.classList.add(styles.noSelect);

    const onMouseMove = (e: MouseEvent) => handleMouseMove(e, thumbIndex);
    const onMouseUp = () => {
      document.body.classList.remove(styles.noSelect);

      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const calculateThumbPosition = (val: number) => {
    if (!sliderRef.current) return 0;
    const percentage =
      ((val - parseFloat(min)) / (parseFloat(max) - parseFloat(min))) * 100;
    return `${percentage}%`;
  };

  const calculatePercentage = (val: number) => {
    if (!sliderRef.current) return 0;
    const percentage =
      ((val - parseFloat(min)) / (parseFloat(max) - parseFloat(min))) * 100;
    return percentage;
  };

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const sliderWidth = rect.width;
    const clickPosition = Math.min(Math.max(e.clientX - rect.left, 0), sliderWidth);

    const percentage = clickPosition / sliderWidth;
    const clickedValue = Math.round(
      parseFloat(min) + percentage * (parseFloat(max) - parseFloat(min)),
    );

    const leftDiff = Math.abs(clickedValue - parseFloat(value[0]));
    const rightDiff = Math.abs(clickedValue - parseFloat(value[1]));

    const updatedValues = [...value] as [string, string];

    if (leftDiff < rightDiff) {
      updatedValues[0] = String(Math.min(clickedValue, parseFloat(value[1]) - 1));
    } else {
      updatedValues[1] = String(Math.max(clickedValue, parseFloat(value[0]) + 1));
    }

    setValue(updatedValues);
  };
  const handleWheel = (e: any, pretext: string) => {
    e.preventDefault();
    const input = pretext === 'from' ? value[0] : value[1];
    const currentValue = parseFloat(input) || 0;
    const delta = e.deltaY > 0 ? -1 : 1;

    // Проверяем новое значение до его установки
    let newValue = currentValue + delta;
    newValue = Math.max(+min, Math.min(newValue, +max ?? Infinity));

    if (newValue < 0) {
      return; // Прерываем выполнение если новое значение меньше нуля
    }

    switch (pretext) {
      case 'from':
        setValue([String(newValue), value[1]]);
        break;
      case 'to':
        setValue([value[0], String(newValue)]);
        break;
    }
  };

  return (
    <div className={styles.rangeSlider}>
      <label className={styles.label}>{fieldLabel}</label>
      <div className={styles.inputs}>
        <InputWithSign
          value={value[0]}
          onClick={handleClick}
          onChange={(e) => {
            const val = processNumberInput(e.target.value);
            setValue([val, value[1]]);
          }}
          onWheel={(e: any) => handleWheel(e, 'from')}
          postfix={postfix}
          prefix="от"
        />
        <InputWithSign
          value={value[1]}
          onClick={handleClick}
          onChange={(e) => {
            const val = processNumberInput(e.target.value);
            setValue([value[0], val]);
          }}
          onWheel={(e: any) => handleWheel(e, 'to')}
          postfix={postfix}
          prefix="до"
        />
      </div>
      <div className={styles.slider} ref={sliderRef}>
        <div className={styles.track} onClick={handleTrackClick}></div>
        <div
          className={`${styles.thumb} ${styles.thumbLeft}`}
          style={{
            left:
              calculatePercentage(parseFloat(value[0])) < 0
                ? '0%'
                : calculateThumbPosition(parseFloat(value[0])),
          }}
          onMouseDown={() => handleThumbMouseDown(0)}
        ></div>
        <div
          className={`${styles.thumb} ${styles.thumbRight}`}
          style={{
            left:
              calculatePercentage(parseFloat(value[1])) > 100
                ? '100%'
                : calculateThumbPosition(parseFloat(value[1])),
          }}
          onMouseDown={() => handleThumbMouseDown(1)}
        ></div>
        <div
          className={styles.range}
          onClick={handleTrackClick}
          style={{
            left: `${Math.max(
              0,
              Math.min(100, calculatePercentage(parseFloat(value[0]))),
            )}%`,
            right: `${Math.max(
              0,
              Math.min(100, 100 - calculatePercentage(parseFloat(value[1]))),
            )}%`,
          }}
        ></div>
      </div>
    </div>
  );
};
